import { Image, List, Popover } from 'antd';
import React, { Fragment, MutableRefObject, ReactElement, useEffect, useRef } from 'react';
import { getPatientPicUrl, getSalutation } from '../../../pages/utils';
import { PatientPersonalProfile } from '../../../store/types/prescription';
import { Patient } from '../../../store/types/patient';
import { getAgeString } from '../../../store/utils';
import { EkaCareIcon } from '../../common/Icons';
import {
    MergePtListItemViewOnly,
    MergePtListItemViewOnlyContent,
} from '../../common/MergePtListItem';
import { SREV2 } from '../../../api/pushGenie';
import { getAclViewClassName } from '../../../restrictions/utils';
import { ONE_CHAR_GENDER } from '../../../pages/NewPatient';
import { RelatedProfilesPopoverWrapper } from '../../RelatedProfiles/RelatedProfilesPopoverWrapper';

const PatientPersonalInfoCard: React.FC<{
    patientPersonalProfile?: PatientPersonalProfile;
    pid?: string;
    onEdit?: () => void;
    formData?: Patient['formData'];
    mergedPts?: Patient[];
    updatePt: () => Promise<void>;
}> = (props) => {
    const { patientPersonalProfile, onEdit, pid, formData, updatePt } = props;
    return patientPersonalProfile ? (
        <PatientPersonalInfoCardFilled
            {...patientPersonalProfile}
            pid={pid}
            onEdit={onEdit}
            mergedPts={props['mergedPts']}
            formData={formData}
            updatePt={updatePt}
        />
    ) : (
        <PatientPersonalInfoCardShimmer />
    );
};

const PatientPersonalInfoCardFilled = (
    props: PatientPersonalProfile & {
        onEdit?: () => void;
        pid?: string;
        mergedPts?: Patient[];
        formData?: Patient['formData'];
        updatePt: () => Promise<void>;
    },
): ReactElement => {
    const { name, age, gender, phone, bloodgroup, onApp, onEdit, pid, formData, updatePt } = props;

    const pic: MutableRefObject<string | undefined> = useRef(getPatientPicUrl(pid));

    const setPic = (id?: string) => {
        pic.current = getPatientPicUrl(id);
    };

    useEffect(() => {
        setPic(pid);
    }, [pid]);

    const ageString = getAgeString(
        {
            age: age?.value,
            month: age?.month,
            dob: age?.dob,
        },
        undefined,
    );
    const uhid = formData?.find((item) => item.key === 'uhid')?.value;

    return (
        <div>
            <div className="flex  justify-between">
                <div className="mt-4">
                    <Image
                        width={45}
                        height={45}
                        src={pic.current}
                        className="bg-neutral-black border border-light rounded-full"
                        preview={{
                            mask: (
                                <span role="img" aria-label="eye" className="anticon anticon-eye">
                                    <svg
                                        viewBox="64 64 896 896"
                                        focusable="false"
                                        data-icon="eye"
                                        width="1em"
                                        height="1em"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z"></path>
                                    </svg>
                                </span>
                            ),
                            maskClassName: 'profile-pic-mask',
                        }}
                    />
                </div>
                {onEdit && (
                    <div
                        onClick={onEdit}
                        className={`flex space-x-4 items-center cursor-pointer mt-2 ${getAclViewClassName(
                            SREV2.PATIENT_DATABASE,
                        )}`}
                    >
                        <div className="flex items-center justify-center">
                            <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                className="fill-actionPrimary"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M10.125 8C9.91406 8 9.75 8.1875 9.75 8.375V11C9.75 11.4219 9.39844 11.75 9 11.75H1.5C1.07812 11.75 0.75 11.4219 0.75 11V3.5C0.75 3.10156 1.07812 2.75 1.5 2.75H4.125C4.3125 2.75 4.5 2.58594 4.5 2.375C4.5 2.1875 4.3125 2 4.125 2H1.47656C0.65625 2 0 2.67969 0 3.5V11C0 11.8438 0.65625 12.5 1.47656 12.5H8.95312C9.77344 12.5 10.4297 11.8438 10.4297 11L10.5 8.375C10.5 8.1875 10.3125 8 10.125 8ZM11.6484 1.50781L10.9922 0.851562C10.7812 0.617188 10.5 0.5 10.1953 0.5C9.91406 0.5 9.63281 0.617188 9.39844 0.851562L3.79688 6.45312C3.58594 6.66406 3.44531 6.92188 3.375 7.22656L3 9.17188C2.95312 9.35938 3.09375 9.52344 3.25781 9.52344C3.28125 9.52344 3.30469 9.5 3.32812 9.5L5.27344 9.125C5.57812 9.05469 5.83594 8.91406 6.04688 8.70312L11.6484 3.10156C12.0938 2.65625 12.0938 1.92969 11.6484 1.50781ZM5.50781 8.16406C5.41406 8.25781 5.27344 8.32812 5.13281 8.35156L3.86719 8.60938L4.125 7.36719C4.14844 7.22656 4.21875 7.08594 4.33594 6.99219L8.50781 2.79688L9.70312 3.99219L5.50781 8.16406ZM11.1328 2.5625L10.2188 3.47656L9.02344 2.28125L9.9375 1.36719C10.0312 1.27344 10.1484 1.27344 10.1953 1.27344C10.2656 1.27344 10.3828 1.27344 10.4766 1.36719L11.1328 2.02344C11.2266 2.11719 11.25 2.23438 11.25 2.30469C11.25 2.35156 11.2266 2.46875 11.1328 2.5625Z" />
                            </svg>
                        </div>
                        <span className="text-actionPrimary font-400 text-12">Edit</span>
                    </div>
                )}
            </div>
            <div>
                <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                        {onApp && (
                            <span className="w-16 h-16 mt-2 pt-app-tooltip-pad">
                                <EkaCareIcon />
                                <div className="tooltip">Connected on eka.care app</div>
                            </span>
                        )}
                        {pid ? (
                            <RelatedProfilesPopoverWrapper
                                pid={pid}
                                placement="rightTop"
                                from="pt-details-name-click"
                                onRefresh={updatePt}
                            >
                                <button className="font-700 text-actionPrimary-active">
                                    {getSalutation(formData)}
                                    {name}
                                </button>
                            </RelatedProfilesPopoverWrapper>
                        ) : (
                            <p className="font-700 text-neutral-3 select-all">{name}</p>
                        )}

                        {props['mergedPts'] && props['mergedPts'].length !== 0 && (
                            <Popover
                                placement="bottomLeft"
                                title={
                                    <div className="font-lato text-darwin-neutral-800 text-12 font-700">
                                        MERGED PATIENTS
                                    </div>
                                }
                                content={
                                    <div className="flex flex-col max-h-592">
                                        <List className="overflow-scroll px-16 py-2">
                                            {props['mergedPts'] &&
                                                props['mergedPts'].length !== 0 &&
                                                props['mergedPts'].filter(Boolean).map((pt) => (
                                                    <List.Item key={pt['id']}>
                                                        <List.Item.Meta
                                                            // avatar={<Avatar src={props.item.imageUrl} />}
                                                            title={
                                                                <MergePtListItemViewOnly pt={pt} />
                                                            }
                                                            description={
                                                                <MergePtListItemViewOnlyContent
                                                                    pt={pt}
                                                                    className="pl-28"
                                                                />
                                                            }
                                                        />
                                                    </List.Item>
                                                ))}
                                        </List>
                                    </div>
                                }
                                trigger="click"
                                className="popover-p-0"
                                overlayStyle={{
                                    width: 300,
                                }}
                                overlayClassName="popover-p-0"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="17"
                                    height="17"
                                    viewBox="0 0 448 512"
                                    style={{
                                        margin: 5,
                                        fill: 'rgba(0, 75, 168, var(--tw-bg-opacity))',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <path d="M81.1 416H366.9c-7.8-54.3-54.4-96-110.9-96H192c-56.4 0-103.1 41.7-110.9 96zM0 96C0 60.7 28.7 32 64 32H384c35.3 0 64 28.7 64 64V416c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V96zM224 272a72 72 0 1 0 0-144 72 72 0 1 0 0 144z" />
                                </svg>
                            </Popover>
                        )}
                    </div>
                </div>
                <div>
                    <p className="uppercase font-700 text-12 text-neutral-5 flex-shrink-0">
                        {ageString}
                        {' | '}
                        {gender ? ONE_CHAR_GENDER[gender?.toLocaleLowerCase()] : ''}{' '}
                        {uhid ? ` | #${uhid}` : ''}
                    </p>
                </div>
                <p className="ml-auto uppercase font-700 text-12 text-neutral-5 tracking-5">
                    {bloodgroup ? (
                        <Fragment>
                            <span style={{ color: '#9f0000' }}> {bloodgroup} </span> {' | '}{' '}
                        </Fragment>
                    ) : (
                        ''
                    )}
                    <span
                        className={`select-all ${getAclViewClassName(SREV2.PATIENT_MOBILE_NUMBER)}`}
                    >
                        {phone?.c} {phone?.n}
                    </span>
                </p>
            </div>
        </div>
    );
};

const PatientPersonalInfoCardShimmer = (): ReactElement => {
    return (
        <Fragment>
            <p className="font-700 text-neutral-3 select-all shine"></p>
            <p className="uppercase font-700 text-12 text-neutral-5 tracking-5 shine"></p>
        </Fragment>
    );
};

export default PatientPersonalInfoCard;
